import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchTrips } from '../services/api';

const SearchTrips = () => {
  const navigate = useNavigate();
  const [searchData, setSearchData] = useState({
    origin: '',
    destination: '',
    date: ''
  });
  const [trips, setTrips] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setSearchData({ ...searchData, [e.target.name]: e.target.value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await searchTrips(searchData);
      setTrips(res.data.trips);
      setSearched(true);
    } catch (err) {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York'
    });
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Find a Ride</h2>

      <div style={styles.searchCard}>
        <form onSubmit={handleSearch}>
          <div style={styles.searchRow}>
            <div style={styles.field}>
              <label style={styles.label}>From</label>
              <input
                style={styles.input}
                type="text"
                name="origin"
                value={searchData.origin}
                onChange={handleChange}
                placeholder="Providence, RI"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>To</label>
              <input
                style={styles.input}
                type="text"
                name="destination"
                value={searchData.destination}
                onChange={handleChange}
                placeholder="New York, NY"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Date</label>
              <input
                style={styles.input}
                type="date"
                name="date"
                value={searchData.date}
                onChange={handleChange}
              />
            </div>
            <button style={styles.searchBtn} type="submit" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {searched && trips.length === 0 && (
        <div style={styles.noResults}>
          No trips found for this route. Try different dates or locations.
        </div>
      )}

      <div style={styles.results}>
        {trips.map(trip => (
          <div
            key={trip.id}
            style={styles.tripCard}
            onClick={() => navigate(`/trips/${trip.id}`)}
          >
            <div style={styles.tripHeader}>
              <div>
                <div style={styles.route}>
                  {trip.origin_address} → {trip.destination_address}
                </div>
                <div style={styles.departure}>
                  {formatDate(trip.departure_time)}
                </div>
              </div>
              <div style={styles.price}>
                {trip.price_suggestion === '0.00' || trip.price_suggestion === 0
                  ? 'Free'
                  : `$${trip.price_suggestion}`}
                {trip.price_negotiable && (
                  <span style={styles.negotiable}> · Negotiable</span>
                )}
              </div>
            </div>

            <div style={styles.tripFooter}>
              <div style={styles.driver}>
                <span style={styles.driverName}>
                  {trip.first_name} {trip.last_name}
                </span>
                {trip.is_verified && (
                  <span style={styles.verified}>✓ Verified</span>
                )}
                <span style={styles.rating}>★ {trip.avg_rating}</span>
              </div>
              <div style={styles.seats}>
                {trip.seats_available} seat{trip.seats_available !== 1 ? 's' : ''} available
              </div>
            </div>

            {trip.notes && (
              <div style={styles.notes}>{trip.notes}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    marginBottom: '24px',
  },
  searchCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    marginBottom: '32px',
  },
  searchRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr auto',
    gap: '16px',
    alignItems: 'end',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#666',
    marginBottom: '6px',
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '15px',
  },
  searchBtn: {
    padding: '12px 24px',
    background: '#4fc3f7',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  error: {
    background: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  noResults: {
    textAlign: 'center',
    color: '#666',
    padding: '40px',
    background: '#f5f5f5',
    borderRadius: '12px',
  },
  results: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  tripCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    cursor: 'pointer',
    transition: 'transform 0.1s',
  },
  tripHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  route: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1a1a2e',
    marginBottom: '4px',
  },
  departure: {
    color: '#666',
    fontSize: '14px',
  },
  price: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#4fc3f7',
  },
  negotiable: {
    fontSize: '14px',
    color: '#666',
    fontWeight: 'normal',
  },
  tripFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  driver: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  driverName: {
    fontWeight: '500',
    color: '#333',
  },
  verified: {
    background: '#e8f5e9',
    color: '#2e7d32',
    padding: '2px 8px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
  },
  rating: {
    color: '#f59e0b',
    fontSize: '14px',
  },
  seats: {
    color: '#666',
    fontSize: '14px',
  },
  notes: {
    marginTop: '12px',
    padding: '12px',
    background: '#f5f5f5',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#555',
  },
};

export default SearchTrips;